import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import TimeAgo from 'react-timeago';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import cs from 'react-timeago/lib/language-strings/cs';
import de from 'react-timeago/lib/language-strings/de';
import deShort from 'react-timeago/lib/language-strings/de-short';
import en from 'react-timeago/lib/language-strings/en';
import enShort from 'react-timeago/lib/language-strings/en-short';
import es from 'react-timeago/lib/language-strings/es';
import esShort from 'react-timeago/lib/language-strings/es-short';
import fr from 'react-timeago/lib/language-strings/fr';
import frShort from 'react-timeago/lib/language-strings/fr-short';
import it from 'react-timeago/lib/language-strings/it';
import itShort from 'react-timeago/lib/language-strings/it-short';
import pt from 'react-timeago/lib/language-strings/pt';
import ptShort from 'react-timeago/lib/language-strings/pt-short';

import { StyleProps, Text, Tooltip } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

type FormatOption = 'long' | 'short';

type Props = {
	format?: FormatOption;
	suffix?: boolean;
};
export interface Formatter {
	prefixAgo?: string;
	prefixFromNow?: string;
	suffixAgo?: string;
	suffixFromNow?: string;
	seconds?: string;
	minute?: string;
	minutes?: string;
	hour?: string;
	hours?: string;
	day?: string;
	days?: string;
	month?: string;
	months?: string;
	year?: string;
	years?: string;
	wordSeparator?: string;
	numbers?: string[];
}

const customizeFormatter = (locale: string, suffix: boolean, format: FormatOption) => {
	const getFormatters = (format: FormatOption) =>
		({
			cs,
			de: format === 'long' ? de : deShort,
			en: format === 'long' ? en : enShort,
			es: format === 'long' ? es : esShort,
			fr: format === 'long' ? fr : frShort,
			it: format === 'long' ? it : itShort,
			pt: format === 'long' ? pt : ptShort,
		}) as Record<string, Formatter>;

	const getFormatter = (locale: string, format: FormatOption) => {
		const formatters = getFormatters(format);
		// User locale can be null or an empty string
		return formatters[locale] || formatters.en;
	};

	const getLongFormatter = (locale: string) => {
		const longFormatters = getFormatters('long');
		// User locale can be null or an empty string
		return longFormatters[locale] || longFormatters.en;
	};

	const createFormatterWithSuffix = (formatter: Formatter, longFormatter: Formatter) => ({
		...formatter,
		prefixAgo: longFormatter.prefixAgo,
		prefixFromNow: longFormatter.prefixFromNow,
		suffixAgo: longFormatter.suffixAgo,
		suffixFromNow: longFormatter.suffixFromNow,
	});

	const formatter = getFormatter(locale, format);
	const longFormatter = getLongFormatter(locale);

	const customFormatter = suffix ? createFormatterWithSuffix(formatter, longFormatter) : formatter;

	return (buildFormatter as any)(customFormatter);
};

export const useFormatter = ({ format = 'short', suffix = true }: Props = {}) => {
	const user = useCurrentUser();
	return customizeFormatter(user.locale, suffix, format);
};

interface RelativeTimeProps extends StyleProps {
	children?: Date;
	date?: Date;
	format?: 'long' | 'short';
	suffix?: boolean; // 'from now' or 'ago' translated suffix
	now?: () => number;
}

export const RelativeTime: FC<RelativeTimeProps> = ({ children, date, suffix = true, format, ...rest }) => {
	const { t } = useTranslation('common');
	const formatter = useFormatter({ suffix, format });

	const label = t('intl_datetime', {
		defaultValue: '',
		value: date || children,
		formatParams: {
			value: {
				day: 'numeric',
				hour12: true,
				hour: 'numeric',
				minute: 'numeric',
				month: 'long',
				year: 'numeric',
			},
		},
	});

	return (
		<Tooltip label={label}>
			<Text as="span" {...rest}>
				<TimeAgo aria-label={label} date={date || children} formatter={formatter} role="timer" title="" />
			</Text>
		</Tooltip>
	);
};
